import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { formatCurrency } from "nhb-toolbox";
import type { Numeric } from "nhb-toolbox/types";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);

/**
 * * Format numeric values as currency format used in BD.
 * @param currency Currency to format.
 * @returns The formatted currency.
 */
export function getFormattedCurrency(currency: Numeric): string {
	return formatCurrency(currency, "BDT", "en-GB");
}

/**
 * * Applies pagination to an array of data.
 *
 * @param data - The array of data to paginate.
 * @param page - The current page number (0-indexed).
 * @param limit - The number of items per page.
 *
 * @returns A new array containing the paginated data for the specified page.
 *
 * @example
 * const paginatedData = applyPagination(data, 1, 10); // For page 1 with 10 items per page
 */
export function applyPagination<T>(data: T[], page: number, limit: number) {
	if (!Array.isArray(data) || data.length === 0) {
		return [];
	}

	return data.slice(page * limit, page * limit + limit);
}

/**
 * * Format `Dayjs` date input in `YYYY-MM-DD` format.
 *
 * @param date Dayjs instance to format in `YYYY-MM-DD` format.
 * @returns Formatted date string.
 */
export function formatDateInput(date: Dayjs): string {
	return dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "";
}

/**
 * * Utility function to check valid date range.
 * @param date1 Start date.
 * @param date2 End date.
 * @returns `true` if the range is valid, otherwise `false`
 */
export function isValidDateRange(
	date1: string | Dayjs,
	date2: string | Dayjs
): boolean {
	const start = typeof date1 === "string" ? dayjs(date1) : date1;
	const end = typeof date2 === "string" ? dayjs(date2) : date2;

	// Ensure both dates are valid Dayjs objects
	if (!start.isValid() || !end.isValid()) return false;

	// Check if start is before or same as end
	return start.isSameOrBefore(end, "day");
}
